import React from "react";
import { Loader2 } from "lucide-react";
import { TID } from "@/lib/testIds";

export const initialPatient = {
    full_name: "", birth_date: "", sex: "", cpf: "", email: "", address: "", city_uf: "",
    zip_code: "", phone: "", insurance: "", caregiver_name: "", caregiver_relationship: "",
    caregiver_phone: "", emergency_contact_name: "", emergency_contact_phone: "", notes: "",
};

/**
 * Reusable patient form used for creation (NewPatient) and editing (PatientEdit).
 * `value` is the current form state; `onChange` receives the whole object.
 */
export default function PatientForm({ value, onChange, onSubmit, loading, error, submitLabel = "Salvar" }) {
    const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });

    return (
        <form onSubmit={onSubmit} className="card-earthy space-y-4">
            <div>
                <label className="label-clinical">Nome completo *</label>
                <input data-testid={TID.patient.fullName} required value={value.full_name || ""} onChange={set("full_name")} className="input-clinical" />
            </div>
            <div className="grid md:grid-cols-3 gap-3">
                <div>
                    <label className="label-clinical">Data de nascimento</label>
                    <input data-testid={TID.patient.birthDate} type="date" value={value.birth_date || ""} onChange={set("birth_date")} className="input-clinical" />
                </div>
                <div>
                    <label className="label-clinical">Sexo</label>
                    <select value={value.sex || ""} onChange={set("sex")} className="input-clinical">
                        <option value="">—</option>
                        <option value="M">Masculino</option>
                        <option value="F">Feminino</option>
                        <option value="O">Outro</option>
                    </select>
                </div>
                <div>
                    <label className="label-clinical">CPF</label>
                    <input data-testid={TID.patient.cpf} value={value.cpf || ""} onChange={set("cpf")} className="input-clinical" />
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="label-clinical">E-mail</label>
                    <input type="email" value={value.email || ""} onChange={set("email")} className="input-clinical" placeholder="paciente@email.com" />
                </div>
                <div>
                    <label className="label-clinical">Telefone</label>
                    <input data-testid={TID.patient.phone} value={value.phone || ""} onChange={set("phone")} className="input-clinical" />
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="label-clinical">Endereço</label>
                    <input value={value.address || ""} onChange={set("address")} className="input-clinical" />
                </div>
                <div>
                    <label className="label-clinical">Cidade / UF</label>
                    <input value={value.city_uf || ""} onChange={set("city_uf")} className="input-clinical" />
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="label-clinical">CEP</label>
                    <input value={value.zip_code || ""} onChange={set("zip_code")} className="input-clinical" />
                </div>
                <div>
                    <label className="label-clinical">SUS / Convênio / Particular</label>
                    <select data-testid={TID.patient.insurance} value={value.insurance || ""} onChange={set("insurance")} className="input-clinical">
                        <option value="">—</option>
                        <option value="SUS">SUS</option>
                        <option value="Convenio">Convênio</option>
                        <option value="Particular">Particular</option>
                    </select>
                </div>
            </div>

            <div className="divider-organic" />
            <h3 className="font-display text-lg font-medium text-brand">Cuidador principal</h3>
            <div className="grid md:grid-cols-3 gap-3">
                <div>
                    <label className="label-clinical">Nome</label>
                    <input data-testid={TID.patient.caregiverName} value={value.caregiver_name || ""} onChange={set("caregiver_name")} className="input-clinical" />
                </div>
                <div>
                    <label className="label-clinical">Parentesco</label>
                    <input value={value.caregiver_relationship || ""} onChange={set("caregiver_relationship")} className="input-clinical" />
                </div>
                <div>
                    <label className="label-clinical">Telefone</label>
                    <input data-testid={TID.patient.caregiverPhone} value={value.caregiver_phone || ""} onChange={set("caregiver_phone")} className="input-clinical" />
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="label-clinical">Contato de emergência (nome)</label>
                    <input value={value.emergency_contact_name || ""} onChange={set("emergency_contact_name")} className="input-clinical" />
                </div>
                <div>
                    <label className="label-clinical">Contato de emergência (telefone)</label>
                    <input value={value.emergency_contact_phone || ""} onChange={set("emergency_contact_phone")} className="input-clinical" />
                </div>
            </div>
            <div>
                <label className="label-clinical">Observações</label>
                <textarea rows={3} value={value.notes || ""} onChange={set("notes")} className="input-clinical" />
            </div>

            {error && <div className="text-sm text-[var(--status-danger)] bg-[var(--status-danger-bg)] border-l-4 border-[var(--status-danger)] p-3 rounded">{error}</div>}

            <button data-testid={TID.patient.submit} disabled={loading} className="btn-primary">
                {loading && <Loader2 size={16} className="animate-spin" />} {submitLabel}
            </button>
        </form>
    );
}
